import { useEffect, useRef, useState } from "react";
import QuestionLayout from "./QuestionLayout";
import AudioPlayButton from "./AudioPlayButton";
import { melodyInput } from "./notation/melodyInput";
import KeySignatureInputWidget from "./KeySignatureInput";

export default function MelodyEditorWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback,
  authoring = false
}) {
  const latestRef = useRef({ value, onChange, mode });
  latestRef.current = { value, onChange, mode };
  const [accidentalToolActive, setAccidentalToolActive] = useState(false);
  const accidentalToolActiveRef = useRef(false);
  const allowAccidentals = authoring || config.allowAccidentals === true;
  const accidentalEnabledRef = useRef(allowAccidentals);
  accidentalEnabledRef.current = allowAccidentals;
  function armAccidentals(active) {
    accidentalToolActiveRef.current = active;
    setAccidentalToolActive(active);
  }
  const containerRef = useRef(null);
  const engineRef = useRef(null);

  // 🔒 stable audio identity
  const audioRef = useRef(value._audio || {});

  // 🔒 non-react storage for notes
  const notesRef = useRef(null);

  // 🔒 ensure we only load once per question
  const hasLoadedRef = useRef(false);

  const audioValue = audioRef.current;

  /* ─────────────────────────────────────────────
     INIT ENGINE (once)
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!containerRef.current) return;

    engineRef.current = new melodyInput(
      "melody-editor" + config.number,
      containerRef.current,
      {
        authoring,
        measures: config.measures,
        timeSig: config.timeSig,
        clef: config.clef,
        lockPitch: config.lockPitch,
        lockRhythm: config.lockRhythm,
        fixedPitch: config.fixedPitch,
        keyText: config.keyText,
        keyTextOffset: config.keyTextOffset,
        disabledMeasures: config.disabledMeasures || []
      },
      {
        // 🔑 ONLY emit changes in edit mode
        setNotes: notes => {
          if (latestRef.current.mode !== "edit") return;

          const frozenNotes = structuredClone(notes);
          notesRef.current = frozenNotes;

          latestRef.current.onChange({
            ...latestRef.current.value,
            notes: frozenNotes,
            _audio: audioRef.current
          });
        }
      }
    );

    return () => {
      engineRef.current = null;
      hasLoadedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─────────────────────────────────────────────
     AUTHORITATIVE LOAD (ONCE)
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!engineRef.current) return;
    if (hasLoadedRef.current) return;

    // 1️⃣ Load structural config
    engineRef.current.load(config);

    notesRef.current = structuredClone(config.melody || []);

    // 2️⃣ Inject student melody ONCE
    if (value?.notes) {
      engineRef.current.load({
        ...config,
        melody: value.notes
      });
      notesRef.current = value.notes;
    }

    if (config.lockRhythm) engineRef.current.hideDurButton();

    hasLoadedRef.current = true;
  }, [config, value?.notes]);

  /* ─────────────────────────────────────────────
     ENABLE / DISABLE ENGINE BY MODE
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!engineRef.current) return;

    // 👇 pick the one your engine supports
    engineRef.current.disabled = mode !== "edit";
    engineRef.current.params.allowAccidentals = allowAccidentals;
    if (mode !== "edit" || !allowAccidentals) {
      armAccidentals(false);
      engineRef.current.axeMenuIsOpen?.hide();
    }
  }, [mode, allowAccidentals]);

  // Match Eartrainer: intercept an armed ordinary click before the staff can
  // delete/place a note, then open its existing picker at the same coordinates.
  // Touch and pen taps generate this same click; no hover/long press is needed.
  useEffect(() => {
    const host = containerRef.current;
    if (!host) return;
    const editAccidental = event => {
      const staff = engineRef.current;
      if (!accidentalEnabledRef.current || !accidentalToolActiveRef.current ||
          latestRef.current.mode !== "edit" || !staff || staff.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.target.dispatchEvent(new MouseEvent("contextmenu", {
        bubbles: true, cancelable: true, view: window,
        clientX: event.clientX, clientY: event.clientY,
        screenX: event.screenX, screenY: event.screenY, button: 2, buttons: 0
      }));
      // A rest, empty space, or a locked measure leaves the tool armed.
      if (staff.axeMenuIsOpen) armAccidentals(false);
    };
    host.addEventListener("click", editAccidental, true);
    return () => host.removeEventListener("click", editAccidental, true);
  }, []);

  // The legacy engine emits note placement changes itself. During authoring,
  // also capture deletion, accidental, tie and keyboard edits after DOM events.
  useEffect(() => {
    if (!authoring || !containerRef.current) return;
    const host = containerRef.current;
    let cancelled = false;
    const capture = () => queueMicrotask(() => {
      if (cancelled || latestRef.current.mode !== "edit" || !engineRef.current || !hasLoadedRef.current) return;
      const notes = engineRef.current.save().melody;
      if (JSON.stringify(notes) === JSON.stringify(notesRef.current)) return;
      notesRef.current = structuredClone(notes);
      latestRef.current.onChange({ ...latestRef.current.value, notes: structuredClone(notes), _audio: audioRef.current });
    });
    const events = ["click", "keyup", "contextmenu", "pointerup"];
    events.forEach(event => host.addEventListener(event, capture));
    return () => { cancelled = true; events.forEach(event => host.removeEventListener(event, capture)); };
  }, [authoring]);

  /* ─────────────────────────────────────────────
     AUDIO STATE UPDATE
  ───────────────────────────────────────────── */

  function setAudioState(audioState) {
    if (mode !== "edit") return;

    audioRef.current = audioState;

    onChange({
      ...value,
      _audio: audioState
    });
  }

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
      horizontalScrollRef={containerRef}
    >
      {/* AUDIO */}
      {config.audio && (
        <div style={{ marginBottom: 12 }}>
          <AudioPlayButton
            src={config.audio}
            value={audioValue}
            onChange={setAudioState}
            mode={mode}
            maxListens={config.maxListens ?? 1}
          />
        </div>
      )}

      {config.image && (
        <div>
          <img width={1000} src={config.image}></img>
        </div>
      )}

      {allowAccidentals && mode === "edit" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
          <button type="button" aria-label="Edit accidentals" aria-pressed={accidentalToolActive}
            onClick={() => {
              engineRef.current?.axeMenuIsOpen?.hide();
              engineRef.current?.removeTester();
              armAccidentals(!accidentalToolActiveRef.current);
            }}
            style={{ minWidth: 44, minHeight: 44, cursor: "pointer", touchAction: "manipulation",
              border: "1px solid currentColor", borderRadius: 6,
              background: accidentalToolActive ? "#dbeafe" : "#fff", color: "#111827" }}>
            ♯♭ Accidentals
          </button>
          <span role="status">{accidentalToolActive
            ? "Tap a note, then choose its accidental. Tap the button again to cancel."
            : "To change an accidental, tap Accidentals, then tap a note."}</span>
        </div>
      )}

      {/* NOTATION EDITOR */}
      <div
        ref={containerRef}
        id={"melody-editor" + config.number}
        style={{
          padding: 10,
          borderRadius: 6,
          maxWidth: 1100,
          overflow: "auto"
        }}
      />
    </QuestionLayout>
  );
}



// The same notation widget serves dictation and composition; identify composition
// explicitly so its palette policy cannot change other question types.
export function MelodyCompositionWidget(props) {
  return <MelodyEditorWidget {...props} config={{...props.config, compositionInput:true}}/>;
}
